import { useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexEntries,
} from 'features/rolodex'
import { ChartDataPoint, DataLayer, TimeRange } from 'features/systemLens'
import {
  TimeSeriesService,
  useResolvedChart,
  useTimeSeriesDataProcessing,
} from 'features/timeSeries'
import type { PointAnnotation } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import type { Ruler } from 'features/timeSeries/components/rulerTool/useRulerTool'
import { SINGLE_SCALE_AXIS_ID } from 'features/timeSeries/components/timeSeriesScatterplot/yAxisOverlayLabels'
import {
  COMPUTED_PEN_PREFIX,
  type ComputedTrendRecord,
} from 'features/timeSeries/computed/computedTrendStore'
import type { EmbeddedTrendView } from 'features/timeSeries/computed/EmbeddedWorkbenchContext'
import { MAX_PENS } from 'features/timeSeries/config/constants'
import { DEFAULT_DATA_LAYERS } from 'features/timeSeries/config/DataLayers'
import { ExperimentLabProvider } from 'features/timeSeries/experiment/ExperimentLabContext'
import { useApplyChartConfig } from 'features/timeSeries/hooks/useApplyChartConfig'
import { useChartSave } from 'features/timeSeries/hooks/useChartSave'
import { useDerivedValues } from 'features/timeSeries/hooks/useDerivedValues'
import type { ActiveSentinel } from 'features/timeSeries/hooks/useSentinelScores'
import { useTimeSeriesUX } from 'features/timeSeries/hooks/useTimeSeriesUX'
import { perfMark } from 'features/timeSeries/perf/processDataPerf'
import { RegressionResult } from 'features/timeSeries/regressionHelpers'
import { SentinelStudioProvider } from 'features/timeSeries/sentinelStudio/SentinelStudioContext'
import {
  type CustomAxisRangeMap,
  hasAnyCustomBound,
  type SensorRangeMap,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { loadDateConfigFromChart } from 'features/timeSeries/utils/chartConfigLoaders'
import {
  ADHOC_CHART_SCOPE,
  chartLocalStateKey,
  clearChartLocalState,
  parseChartLocalState,
  readChartLocalState,
  writeChartLocalState,
} from 'features/timeSeries/utils/chartLocalState'
import { buildChartPatchData } from 'features/timeSeries/utils/chartSerialization'
import {
  buildDataProcessingConfigsFromState,
  buildDateConfigFromState,
  buildSourceConfigsFromState,
  buildYAxisModeFromState,
} from 'features/timeSeries/utils/chartStateBuilders'
import type { FilteredPointsSeries } from 'features/timeSeries/utils/dataProcessing'
import {
  buildTimeRangeKey,
  getScatterplotIsInitializing,
} from 'features/timeSeries/utils/scatterplotInitialization'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  BaseSensorEntry,
  ChartInteractionMode,
  ChartTimeUnit,
  DateMode,
  LocalThresholdOverrideType,
  RegressionConfig,
  ResampleConfig,
  RolodexEntryType,
  SensorAxisAssignment,
  SensorAxisMapping,
  SensorEntryWrapper,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { CursorDataProvider } from './CursorDataContext'

interface ScatterplotProviderProps {
  children: ReactNode
  chartID: string
  siteID: string
  /** Display-only overlay; pens and workbench tools operate on real sensors. */
  computedOverlay?: ComputedTrendRecord | null
  disableSaving?: boolean
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
  /** Initial sensor IDs to load when no chart config exists (e.g., from URL params) */
  initialSensorIds?: string[]
  /**
   * Seed custom line colors per sensor id (applied once per id). Lets callers
   * pin specific colors — e.g. a primary sensor in green and related ones in
   * translucent blue/teal/purple — without the user picking them by hand.
   */
  initialSensorColors?: Record<string, string>
  /**
   * Sensor IDs rendered de-emphasized (dotted + translucent) so a primary
   * sensor stands out against related/redundant ones overlaid on the chart.
   */
  deemphasizedSensorIds?: string[]
  /** Initial time range to display; falls back to the last 7 days when omitted */
  initialTimeRange?: { from: string; to: string }
  /**
   * Sensor IDs that override the loaded chart selection. `undefined` leaves the
   * selection alone. An empty list only means something together with
   * `preserveManualSensors`: it removes the pens the previous override put on
   * the chart and keeps the ones the user added by hand.
   */
  overrideSensorIds?: string[]
  /**
   * When true, applying `overrideSensorIds` PRESERVES sensors the user added
   * manually (in-chart tag-search / pen drawer) instead of replacing the whole
   * selection. The override set is still add/remove-authoritative for the
   * sensors it controls; only manually-added extras survive an override change.
   */
  preserveManualSensors?: boolean
  /**
   * Time range that should override the loaded chart viewport once on mount.
   * An explicit bin size wins over the pen-count default.
   */
  overrideTimeRange?: {
    from: string
    to: string
    interval?: number
    intervalUnit?: ChartTimeUnit
  }
  /**
   * Per-pen y-axis bounds applied on top of the chart's own ranges whenever
   * they change (the agent's axis requests on an embedded trend).
   */
  overrideCustomAxisRanges?: CustomAxisRangeMap
  /**
   * One axis for every pen with these bounds, applied whenever they change
   * (the agent's shared-axis request on an embedded trend).
   */
  overrideSharedAxisRange?: { min?: number; max?: number }
  /**
   * Pens hidden on top of the chart's own visibility whenever the list
   * changes; pens dropped from the list become visible again (the agent's hide
   * requests on an embedded trend).
   */
  overrideHiddenSensorIds?: string[]
  /**
   * Reports the viewport, pens, hidden pens and axis bounds whenever the
   * user changes them, once initialization has settled (embedded trends).
   */
  onViewChange?: (view: EmbeddedTrendView) => void
  /**
   * Ad-hoc mode: skip resolving `chartID` (no rolodex fetch / no 404 toast)
   * while keeping save functional. First save promotes the transient slug
   * into a persisted Chart entry.
   */
  transient?: boolean
  /** Seed value for the editable chart name (used by ad-hoc views). */
  initialChartName?: string
  /**
   * Persist live chart state to localStorage (debounced) and hydrate it on
   * mount with precedence over the rolodex-saved config. Keyed by site +
   * chart id (or an `adhoc` scope for transient charts). Off by default so
   * embedded/secondary chart surfaces are unaffected; the standalone Process
   * Data pages opt in.
   */
  persistLocalState?: boolean
  /**
   * Override the localStorage scope (the `<scope>` segment of the key) instead
   * of deriving it from chart id / `adhoc`. Doc-viewer tabs pass their stable
   * tab id here so each tab persists to its OWN key: the transient slug those
   * tabs mount with regenerates every mount and cannot key anything, and a
   * shared `adhoc` scope would clobber across simultaneously open tabs.
   *
   * Two additional consequences vs. the default scoping:
   *  - the seed props (`initialSensorIds`/`initialTimeRange`/overrides) become
   *    the tab's INITIAL content only; stored local state, when present, is a
   *    superset the user has since edited and takes precedence over them (a
   *    plain seed normally suppresses hydration — see `hasExplicitSeeds`).
   *  - a save does NOT migrate the key to the newly-minted chart id (as the
   *    ad-hoc page does): the tab stays open under its tab id, so its state
   *    stays keyed by the tab id and simply reconverges with the saved chart.
   */
  localStateScope?: string
  /**
   * Treat this mount's seeds as a deep link even under a `localStateScope`:
   * a fresh navigation to an alert / signal window must show what it links
   * to, so stored scoped state is skipped (though still written going
   * forward). Without this, revisiting a link whose scope had persisted an
   * earlier session's pens and range silently restores that state instead
   * of the linked window.
   */
  deepLinkSeeds?: boolean
}

interface ScatterplotProviderReturnType {
  siteID: string
  organizationID: string
  chartID: string
  transient: boolean
  chartData: ChartDataPoint[]
  interpolate: boolean
  setInterpolate: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  isInitializing: boolean
  liveTail: boolean
  setLiveTail: Dispatch<SetStateAction<boolean>>
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  setDerivedValues: Dispatch<SetStateAction<TimeSeriesDerivedValueEntryJSON[]>>
  regressionConfigs: RegressionConfig[]
  setRegressionConfigs: Dispatch<SetStateAction<RegressionConfig[]>>
  regressionResults: Record<string, RegressionResult>
  setRegressionResults: Dispatch<
    SetStateAction<Record<string, RegressionResult>>
  >
  unitMapping: Record<string, string>
  computedScalePairs: Map<string, string>
  sensorRanges: SensorRangeMap
  localThresholdOverrides: LocalThresholdOverrideType
  setLocalThresholdOverrides: Dispatch<
    SetStateAction<LocalThresholdOverrideType>
  >
  thresholdFilters: Record<string, ThresholdFilterConfig>
  setThresholdFilters: Dispatch<
    SetStateAction<Record<string, ThresholdFilterConfig>>
  >
  /** Points the pen filters excluded, for the chart's exclusion marks. */
  filteredPoints: FilteredPointsSeries[]
  smoothingConfigs: Record<string, SmoothingConfig>
  setSmoothingConfigs: Dispatch<SetStateAction<Record<string, SmoothingConfig>>>
  /** Per-pen training-bucket views. */
  resampleConfigs: Record<string, ResampleConfig>
  setResampleConfigs: Dispatch<SetStateAction<Record<string, ResampleConfig>>>
  singleScaleMode: boolean
  autoScaleMode: boolean
  setAutoScaleMode: Dispatch<SetStateAction<boolean>>
  handleScaleModeToggle: (newSingleScaleMode: boolean) => void
  handleClearAxisRange: (sensorId: string) => void
  handleAutoScaleAll: () => void
  connectPoints: boolean
  setConnectPoints: Dispatch<SetStateAction<boolean>>
  xAxisSensorId: string | null
  setXAxisSensorId: Dispatch<SetStateAction<string | null>>
  customAxisRanges: Record<string, { min?: number; max?: number }>
  customXAxisRange: { min?: number; max?: number }
  setCustomXAxisRange: Dispatch<SetStateAction<{ min?: number; max?: number }>>
  sensorAxisMapping: SensorAxisMapping
  fetchRawData: ReturnType<typeof useTimeSeriesDataProcessing>['fetchRawData']
  prefetchTimeRange: ReturnType<
    typeof useTimeSeriesDataProcessing
  >['prefetchTimeRange']
  handleRemoveDerivedValue: (id: string) => Promise<void>
  createDerivedValues: (
    newDerived: TimeSeriesDerivedValueEntryJSON,
    editingDerivedId: string | null
  ) => Promise<TimeSeriesDerivedValueEntryJSON[]>
  dataLayers: DataLayer[]
  setDataLayers: Dispatch<SetStateAction<DataLayer[]>>
  setCustomAxisRanges: Dispatch<
    SetStateAction<Record<string, { min?: number; max?: number }>>
  >
  expandedAxes: Record<string, boolean>
  setExpandedAxes: Dispatch<SetStateAction<Record<string, boolean>>>
  setSensorAxisAssignment: Dispatch<SetStateAction<SensorAxisAssignment>>
  handleYMinChange: (sensorId: string, value: string) => void
  handleYMaxChange: (sensorId: string, value: string) => void
  timeRange: TimeRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  selectedSensors: BaseSensorEntry[]
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  sensorEntries: BaseSensorEntry[]
  /** Pen-cap state. Consumers MUST surface `isCapped` rather than hide it. */
  penCap: {
    max: number
    requested: number
    shown: number
    isCapped: boolean
    showAll: () => void
  }
  dateMode: DateMode
  setDateMode: React.Dispatch<React.SetStateAction<DateMode>>
  /**
   * Leave live tail because the user navigated to a specific window.
   *
   * Use this rather than `setDateMode('absolute')`. That is the mode *toggle*:
   * it restores the last absolute window, which clobbers a range set in the
   * same interaction.
   */
  exitLiveTail: () => void
  customSensorColors: Record<string, string>
  handleChangeColor: (sensorId: string, color: string) => void
  sensorVisibilityConfig: Record<string, boolean>
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  relativeStart: { value?: number; unit: ChartTimeUnit }
  setRelativeStart: Dispatch<
    SetStateAction<{ value?: number; unit: ChartTimeUnit }>
  >
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  setRelativeEnd: Dispatch<
    SetStateAction<{ isNow: boolean; value?: number; unit: ChartTimeUnit }>
  >
  cadenceSeconds: number
  setCadenceSeconds: Dispatch<SetStateAction<number>>
  chartName: string
  setChartName: Dispatch<SetStateAction<string>>
  /**
   * Persist the chart. Pass `nameOverride` to save with a specific name without
   * waiting for a `setChartName` state flush — the save-name prompt uses this so
   * accepting the pre-filled default (no state change) still saves.
   */
  saveChart: (nameOverride?: string) => Promise<void>
  revertChanges: () => void
  hasUnsavedChanges: boolean
  hasUnsavedAnnotationChanges: boolean
  /**
   * True only for a brand-new chart that has never been persisted (a transient
   * slug before its first save promotes it). Drives the "prompt for a name on
   * first save" gate; re-saves of an existing chart are silent.
   */
  isNewChart: boolean
  lastSavedAt: Date | null
  isResizing: boolean
  setIsResizing: Dispatch<SetStateAction<boolean>>
  highlightedSensorId: string | null
  setHighlightedSensorId: Dispatch<SetStateAction<string | null>>
  secondaryHighlightedSensorIds: string[]
  setSecondaryHighlightedSensorIds: Dispatch<SetStateAction<string[]>>
  hideAllAlerts: boolean
  setHideAllAlerts: Dispatch<SetStateAction<boolean>>
  /**
   * Which of the site's process-data labels are drawn over the chart. Empty by
   * default: a site's labels are opt-in per label, added from the sidebar's
   * Labels tab. The labels themselves are site-bound Rolodex entries
   * (`useProcessDataLabels`), not chart state, so only the selection lives here.
   */
  visibleLabelIds: string[]
  setVisibleLabelIds: Dispatch<SetStateAction<string[]>>
  deemphasizedSensorIds: string[]
  timeXAxisPreviewRange: { min: number; max: number } | null
  setTimeXAxisPreviewRange: Dispatch<
    SetStateAction<{ min: number; max: number } | null>
  >
  resetViewport: () => void
  pendingSensorIds: string[] | undefined
  loadTriggerRef: React.MutableRefObject<'zoom' | 'other'>
  /**
   * Ruler / point-annotation / open-tool state.
   *
   * Owned here rather than inside `useRulerTool` / `usePointAnnotationTool` so
   * that `useChartSave` can read it and `applyChartConfig` can restore it. The
   * tool hooks still own every mutation; the provider only holds the state.
   */
  rulers: Ruler[]
  setRulers: Dispatch<SetStateAction<Ruler[]>>
  pointAnnotations: PointAnnotation[]
  setPointAnnotations: Dispatch<SetStateAction<PointAnnotation[]>>
  interactionMode: ChartInteractionMode
  setInteractionMode: Dispatch<SetStateAction<ChartInteractionMode>>
  /**
   * Sentinels added to this chart. Persisted with the chart as `'sentinel'`
   * source configs (entry id + target + chosen method; overlay visibility
   * rides the shared `hidden` field) and restored by `applyChartConfig`.
   */
  activeSentinels: ActiveSentinel[]
  /** Adds the sentinel's target tag as a pen and turns the Sentinels layer on. */
  addSentinel: (
    entryId: string,
    target: string,
    name?: string,
    meta?: {
      windowStart?: string
      windowEnd?: string
      splitDate?: string
      bandK?: number
      showSplitRegions?: boolean
      showOutOfBandMarks?: boolean
    }
  ) => void
  /** Removes the overlay; the target pen stays (remove it from Pens if unwanted). */
  removeSentinel: (entryId: string) => void
  setSentinelMethod: (entryId: string, method: string, bandK?: number) => void
  /** Per-sentinel overlay visibility; false hides the band/readouts. */
  sentinelVisibilityConfig: Record<string, boolean>
  setSentinelVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  computedOverlay: ComputedTrendRecord | null
}

const EMPTY_DEEMPHASIZED: string[] = []

/** Debounce for the continuous Local State writer. */
const LOCAL_STATE_WRITE_DEBOUNCE_MS = 750

/** The latest-1-week relative view every unsaved chart starts from and
    reverts to; `resetViewport` and `revertChanges` must agree on it. */
const buildDefaultRelativeDateState = (
  penCount: number
): {
  dateMode: DateMode
  timeRange: TimeRange
  liveTail: boolean
  relativeStart: { value: number; unit: ChartTimeUnit }
  relativeEnd: { isNow: boolean; value: number; unit: ChartTimeUnit }
} => {
  const now = dayjs()
  const from = now.subtract(7, 'days').toISOString()
  const to = now.toISOString()
  const { interval, interval_unit } = TimeSeriesService.getIntervalForPenCount(
    now.diff(dayjs(from), 'seconds'),
    penCount
  )
  return {
    dateMode: 'relative',
    timeRange: { from, to, interval, intervalUnit: interval_unit },
    liveTail: false,
    relativeStart: { value: 7, unit: ChartTimeUnit.DAYS },
    relativeEnd: { isNow: true, value: 0, unit: ChartTimeUnit.DAYS },
  }
}

export const ScatterplotProvider = ({
  children,
  chartID,
  siteID,
  disableSaving = false,
  onChartSaved,
  initialSensorIds,
  initialSensorColors,
  deemphasizedSensorIds,
  initialTimeRange,
  overrideSensorIds,
  overrideTimeRange,
  overrideCustomAxisRanges,
  overrideSharedAxisRange,
  overrideHiddenSensorIds,
  onViewChange,
  preserveManualSensors = false,
  transient = false,
  initialChartName,
  persistLocalState = false,
  localStateScope,
  computedOverlay = null,
  deepLinkSeeds = false,
}: ScatterplotProviderProps) => {
  const { t } = useLingui()

  const [rulers, setRulers] = useState<Ruler[]>([])
  const [pointAnnotations, setPointAnnotations] = useState<PointAnnotation[]>(
    []
  )
  const [interactionMode, setInteractionMode] =
    useState<ChartInteractionMode>('none')
  const [visibleLabelIds, setVisibleLabelIds] = useState<string[]>([])
  const [dataLayers, setDataLayers] = useState<DataLayer[]>(DEFAULT_DATA_LAYERS)
  const [connectPoints, setConnectPoints] = useState(true)
  const [isResizing, setIsResizing] = useState(false)
  const [chartName, setChartName] = useState<string>(initialChartName ?? '')
  const { errorNotification } = useGeckoNotifications()

  const { getSite } = useRolodexEntries(
    siteID ? { ids: [siteID], type: [RolodexEntryType.SITE] } : undefined
  )
  const orgID = useMemo(() => {
    const site = getSite(siteID)
    return site?.tags.organization ?? ''
  }, [siteID, getSite])

  const { sites } = useRolodexEntries({
    type: RolodexEntryType.SITE,
    ids: [siteID],
    limit: 1,
  })

  const organizationID = useMemo(() => {
    return sites.at(0)?.tags.organization ?? ''
  }, [sites])

  const {
    chart,
    error: chartError,
    isLoading: isLoadingChart,
  } = useResolvedChart(disableSaving || transient ? undefined : chartID, {
    onError: (error) => {
      errorNotification(
        t`Failed to resolve chart ${chartID}: ${{ value433_65: error.message }}`
      )
    },
  })

  const localStateEnabled = persistLocalState && !disableSaving

  /**
   * A caller-supplied `localStateScope` wins (doc-viewer tab id); otherwise
   * saved charts key by chart id and the ad-hoc page keys by a stable `adhoc`
   * scope because its transient slug regenerates every mount. Held in a ref so
   * a save that promotes an ad-hoc chart can redirect subsequent writes to the
   * new chart-id key (see `handleChartSavedInternal`).
   *
   * INVARIANT: this key (like `hydratedLocalState` and `configChart` below) is
   * frozen at mount, so every persistLocalState surface must REMOUNT the
   * provider when its scope changes in place — otherwise the writer would
   * keep mirroring the new chart's state under the old chart's key.
   * ProcessDataWorkbench enforces this by keying its provider subtree with
   * chartScopeKey; doc-viewer tabs mount one provider per tab id
   * (KeepAlivePanel key={tab.id}), and the scope IS that tab id, so it is
   * stable for the provider's whole lifetime.
   */
  const localStateKeyRef = useRef(
    chartLocalStateKey(
      siteID,
      localStateScope ?? (transient ? ADHOC_CHART_SCOPE : chartID)
    )
  )

  /**
   * Deep links must show what they link to: any explicit URL/route seed
   * (sensors, time range, alert overrides) disables local-state hydration for
   * this mount. The debounced writer still runs, so the deep-linked state
   * becomes the local state going forward.
   *
   * The exception is a scoped surface (`localStateScope`, i.e. a doc-viewer
   * tab): there the seed is not a deep link but the tab's own initial content,
   * and stored local state is a superset the user has since edited — so stored
   * state must win over the seed rather than be suppressed by it.
   */
  const hasExplicitSeeds = Boolean(
    (initialSensorIds?.length ?? 0) > 0 ||
    initialTimeRange ||
    (overrideSensorIds?.length ?? 0) > 0 ||
    overrideTimeRange
  )

  /**
   * Local state read once per mount. When present it wins over the saved chart
   * config: the apply effect below hydrates from it INSTEAD of the saved chart,
   * while `useChartSave` keeps comparing against the saved chart, so the
   * unsaved-changes indicator survives a refresh whenever local != saved.
   */
  const [hydratedLocalState] = useState(() => {
    if (!localStateEnabled) return null
    if (hasExplicitSeeds && (!localStateScope || deepLinkSeeds)) return null
    const stored = readChartLocalState(localStateKeyRef.current)
    if (!stored) return null
    const localChart = parseChartLocalState(stored)
    return localChart ? { chart: localChart, view: stored.view } : null
  })

  /**
   * Seed sensors to apply additively on mount — but only when local state did
   * NOT hydrate. When it did (a scoped-surface refresh), the stored selection
   * is authoritative: re-applying the seed would resurrect pens the user
   * removed, and holding `isInitializing` open waiting for a seed we never
   * apply would wedge the chart (see `getScatterplotIsInitializing`).
   */
  const seedInitialSensorIds = hydratedLocalState ? undefined : initialSensorIds

  // The site-wide sensor list backs the pen picker, and at a real site it is
  // large: longridge-ac0a01 returns 6,400 entries / 5.6MB in ~1.8-2.9s. Loading
  // it is fine; blocking the chart on it is not, so it is deliberately kept off
  // the critical path (see `resolutionSensorEntries` below).
  const { data: allSensorEntryWrappers } = useGetRolodexEntriesByLoadOptions<
    SensorEntryWrapper[]
  >({
    type: [RolodexEntryType.DATA_POINTS],
    tags: { site: [siteID] },
    limit: 10000,
  })

  // Only the pens this chart actually needs. The chart names them up front, so
  // they can be fetched directly instead of waiting for the whole site.
  const requestedSensorIds = useMemo(() => {
    const ids = new Set<string>()
    chart?.sourceConfigs
      ?.filter((sourceConfig) => sourceConfig.type === 'sensor')
      .forEach((sourceConfig) => ids.add(sourceConfig.sourceId))
    // Local-state pens hydrate through the same resolution pool, so they must
    // be part of the targeted fetch too.
    hydratedLocalState?.chart.sourceConfigs
      ?.filter((sourceConfig) => sourceConfig.type === 'sensor')
      .forEach((sourceConfig) => ids.add(sourceConfig.sourceId))
    initialSensorIds?.forEach((id) => ids.add(id))
    overrideSensorIds?.forEach((id) => ids.add(id))
    return [...ids]
  }, [chart, hydratedLocalState, initialSensorIds, overrideSensorIds])

  const [showAllPens, setShowAllPens] = useState(false)

  /**
   * Pens actually loaded, capped unless the user opts out.
   *
   * Cost scales with pens x buckets, and a chart exists in dev plotting all
   * 2,153 sensors at its site: ~21M points, minutes per refresh. Past a few
   * dozen lines the chart is also unreadable regardless of how fast it loads,
   * so the cap is as much about legibility as cost.
   *
   * Capping here rather than at render is deliberate: this list drives both the
   * entry fetch and (via applyChartConfig) the selection, so the pens we do not
   * show are pens we never pay for.
   */
  const resolutionSensorIds = useMemo(
    () =>
      showAllPens ? requestedSensorIds : requestedSensorIds.slice(0, MAX_PENS),
    [requestedSensorIds, showAllPens]
  )

  /**
   * Never cap silently — a chart quietly showing less than it was asked to is
   * the exact failure this page has been plagued by. Consumers must surface
   * this.
   */
  const penCap = useMemo(
    () => ({
      max: MAX_PENS,
      requested: requestedSensorIds.length,
      shown: resolutionSensorIds.length,
      isCapped: resolutionSensorIds.length < requestedSensorIds.length,
      showAll: () => setShowAllPens(true),
    }),
    [requestedSensorIds, resolutionSensorIds]
  )

  const {
    data: resolutionSensorWrappers,
    error: resolutionSensorEntriesError,
    isLoading: isLoadingResolutionSensorEntries,
  } = useGetRolodexEntriesByLoadOptions<SensorEntryWrapper[]>(
    resolutionSensorIds.length > 0
      ? {
          type: [RolodexEntryType.DATA_POINTS],
          ids: resolutionSensorIds,
          limit: resolutionSensorIds.length,
        }
      : undefined
  )

  const {
    derivedValues: derivedValueEntries,
    error: derivedValueEntriesError,
    hasLoaded: derivedValueEntriesLoaded,
    isLoading: isLoadingDerivedValueEntries,
  } = useDerivedValues({
    tags: { site: [siteID] },
  })

  /** Full site list. Powers the pen picker; must not gate the chart. */
  const sensorEntries = useMemo(
    () => allSensorEntryWrappers?.map((entry) => entry.entry) ?? [],
    [allSensorEntryWrappers]
  )

  /**
   * The pool used to resolve the chart's own pens and any caller-supplied
   * sensor ids. Every consumer of this pool looks up ids it already knows
   * (chart source configs, initialSensorIds, overrideSensorIds), so a targeted
   * fetch is sufficient — and it returns in a fraction of the time the
   * site-wide list takes.
   *
   * Falls back to the site-wide list once that arrives, so a sensor missing
   * from the targeted response still resolves rather than silently vanishing.
   */
  const targetedSensorEntries = useMemo(
    () => resolutionSensorWrappers?.map((entry) => entry.entry) ?? [],
    [resolutionSensorWrappers]
  )

  /**
   * Identity matters here: this feeds `applyChartConfig`, which re-applies the
   * saved chart over live state whenever it re-runs. Once the targeted fetch has
   * resolved every id, the site-wide list arriving later must NOT produce a new
   * array — that would re-apply the chart config on top of whatever the user had
   * changed in the meantime.
   */
  const resolutionSensorEntries = useMemo(() => {
    if (targetedSensorEntries.length === resolutionSensorIds.length) {
      return targetedSensorEntries
    }

    const byId = new Map(
      targetedSensorEntries.map((sensor) => [sensor.id, sensor])
    )
    for (const sensor of sensorEntries) {
      if (!byId.has(sensor.id)) byId.set(sensor.id, sensor)
    }
    return [...byId.values()]
  }, [targetedSensorEntries, resolutionSensorIds, sensorEntries])

  const resolutionSensorEntriesLoaded =
    resolutionSensorIds.length === 0 ||
    resolutionSensorWrappers !== undefined ||
    resolutionSensorEntriesError !== undefined ||
    allSensorEntryWrappers !== undefined

  /**
   * Entries for anything that can end up selected: the chart's own pens plus
   * whatever the user adds from the pen picker (which only exists once the
   * site-wide list has loaded). Threshold lookup needs the selected sensors'
   * entries, so it reads from here rather than either list alone.
   */
  const sensorEntryPool = useMemo(() => {
    if (sensorEntries.length === 0) return resolutionSensorEntries
    const byId = new Map(sensorEntries.map((sensor) => [sensor.id, sensor]))
    for (const sensor of resolutionSensorEntries) {
      if (!byId.has(sensor.id)) byId.set(sensor.id, sensor)
    }
    return [...byId.values()]
  }, [sensorEntries, resolutionSensorEntries])

  const [selectedSensors, setSelectedSensors] = useState<BaseSensorEntry[]>([])
  const [activeSentinels, setActiveSentinels] = useState<ActiveSentinel[]>([])

  const addSentinel = useCallback(
    (
      entryId: string,
      target: string,
      name?: string,
      meta?: {
        windowStart?: string
        windowEnd?: string
        splitDate?: string
        bandK?: number
        showSplitRegions?: boolean
        showOutOfBandMarks?: boolean
      }
    ) => {
      const targetEntry = sensorEntryPool.find((sensor) => sensor.id === target)
      if (!targetEntry) {
        errorNotification(
          t`Sentinel target tag ${target} was not found at this site`
        )
        return
      }
      setActiveSentinels((prev) =>
        prev.some((sentinel) => sentinel.entryId === entryId)
          ? prev
          : [
              ...prev,
              {
                entryId,
                target,
                name,
                windowStart: meta?.windowStart,
                windowEnd: meta?.windowEnd,
                splitDate: meta?.splitDate,
                bandK: meta?.bandK,
                showSplitRegions: meta?.showSplitRegions,
                showOutOfBandMarks: meta?.showOutOfBandMarks,
              },
            ]
      )
      setSelectedSensors((prev) =>
        prev.some((sensor) => sensor.id === target)
          ? prev
          : [...prev, targetEntry]
      )
      setDataLayers((prev) =>
        prev.map((layer) =>
          layer.id === 'sentinels' ? { ...layer, enabled: true } : layer
        )
      )
    },
    [sensorEntryPool, errorNotification, t]
  )

  const removeSentinel = useCallback((entryId: string) => {
    setActiveSentinels((prev) =>
      prev.filter((sentinel) => sentinel.entryId !== entryId)
    )
  }, [])

  const [sentinelVisibilityConfig, setSentinelVisibilityConfig] = useState<
    Record<string, boolean>
  >({})

  const setSentinelMethod = useCallback(
    (entryId: string, method: string, bandK?: number) => {
      setActiveSentinels((prev) =>
        prev.map((sentinel) =>
          sentinel.entryId === entryId
            ? { ...sentinel, method, ...(bandK !== undefined ? { bandK } : {}) }
            : sentinel
        )
      )
    },
    []
  )

  const [timeXAxisPreviewRange, setTimeXAxisPreviewRange] = useState<{
    min: number
    max: number
  } | null>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    if (initialTimeRange) {
      const from = dayjs(initialTimeRange.from)
      const to = dayjs(initialTimeRange.to)
      const rangeSeconds = to.diff(from, 'seconds')
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          rangeSeconds,
          initialSensorIds?.length ?? 0
        )
      return {
        from: initialTimeRange.from,
        to: initialTimeRange.to,
        preset: 'custom',
        interval,
        intervalUnit: interval_unit,
      }
    }
    const now = new Date().toISOString()
    return { from: dayjs(now).subtract(7, 'days').toISOString(), to: now }
  })

  // Track derived value IDs that should be deleted on save
  const derivedValueIdsToDeleteRef = useRef<Set<string>>(new Set())

  const [relativeStart, setRelativeStart] = useState<{
    value?: number
    unit: ChartTimeUnit
  }>({
    value: 7,
    unit: 'days',
  })
  const [relativeEnd, setRelativeEnd] = useState<{
    isNow: boolean
    value?: number
    unit: ChartTimeUnit
  }>({
    isNow: true,
    value: 0,
    unit: 'days',
  })

  const {
    chartData,
    interpolate,
    setInterpolate,
    isLoading,
    liveTail,
    setLiveTail,
    derivedValues,
    setDerivedValues,
    regressionConfigs,
    setRegressionConfigs,
    regressionResults,
    setRegressionResults,
    unitMapping,
    computedScalePairs,
    localThresholdOverrides,
    setLocalThresholdOverrides,
    thresholdFilters,
    setThresholdFilters,
    filteredPoints,
    smoothingConfigs,
    setSmoothingConfigs,
    resampleConfigs,
    setResampleConfigs,
    fetchRawData,
    handleRemoveDerivedValue,
    createDerivedValues,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    dateMode,
    setDateMode,
    exitLiveTail,
    customSensorColors,
    setCustomSensorColors,
    handleChangeColor,
    cadenceSeconds,
    setCadenceSeconds,
    loadTriggerRef,
    prefetchTimeRange,
  } = useTimeSeriesDataProcessing({
    sensorEntries: sensorEntryPool,
    selectedSensors,
    setSelectedSensors,
    timeRange,
    setTimeRange,
    dataLayers,
    siteID,
    orgID,
    derivedValueIdsToDeleteRef,
    relativeStart,
    relativeEnd,
    initialDateMode: initialTimeRange ? 'absolute' : undefined,
    computedSeries: computedOverlay?.series,
  })

  const {
    singleScaleMode,
    setSingleScaleMode,
    autoScaleMode,
    setAutoScaleMode,
    handleScaleModeToggle,
    handleClearAxisRange,
    handleAutoScaleAll,
    xAxisSensorId,
    setXAxisSensorId,
    customAxisRanges,
    customXAxisRange,
    setCustomXAxisRange,
    sensorAxisMapping,
    setCustomAxisRanges,
    expandedAxes,
    setExpandedAxes,
    setSensorAxisAssignment,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    highlightedSensorId,
    setHighlightedSensorId,
    secondaryHighlightedSensorIds,
    setSecondaryHighlightedSensorIds,
    hideAllAlerts,
    setHideAllAlerts,
  } = useTimeSeriesUX({
    selectedSensors,
    derivedValues,
    unitMapping,
    chartData,
  })

  /**
   * The Experiment Lab's trained band regularly extends past the target
   * pen's own data range; with scales pinned to pen data the band clips off
   * the plot. The provider below reports the band's (percentile-guarded)
   * y-extent per target pen, and it's merged into `sensorRanges` HERE — the
   * one source every consumer (uPlot scales, Ymin/Ymax overlay labels,
   * ruler) reads — so the widened axis stays consistent everywhere. Custom
   * user bounds still win downstream via resolveYAxisRange.
   */
  const [experimentYExtents, setExperimentYExtents] = useState<
    Record<string, { min: number; max: number }>
  >({})
  const reportExperimentYExtents = useCallback(
    (next: Record<string, { min: number; max: number }>) => {
      setExperimentYExtents((prev) => {
        const prevKeys = Object.keys(prev)
        const nextKeys = Object.keys(next)
        const unchanged =
          prevKeys.length === nextKeys.length &&
          nextKeys.every(
            (key) =>
              prev[key]?.min === next[key].min &&
              prev[key]?.max === next[key].max
          )
        return unchanged ? prev : next
      })
    },
    []
  )
  const sensorRangesWithExperiment = useMemo(() => {
    const extents = Object.entries(experimentYExtents)
    if (!extents.length) return sensorRanges
    let out = sensorRanges
    let copied = false
    for (const [sensorId, extent] of extents) {
      const current = out[sensorId]
      // Only widen a pen that's actually on the chart with settled data;
      // the band alone should not conjure an axis for an absent pen.
      if (!current) continue
      const min =
        current.min !== undefined
          ? Math.min(current.min, extent.min)
          : extent.min
      const max =
        current.max !== undefined
          ? Math.max(current.max, extent.max)
          : extent.max
      if (min === current.min && max === current.max) continue
      if (!copied) {
        out = { ...out }
        copied = true
      }
      out[sensorId] = { ...current, min, max }
    }
    return out
  }, [sensorRanges, experimentYExtents])

  const { applyChartConfig } = useApplyChartConfig()

  /**
   * The saved chart is applied ONCE per chartID, not whenever a dependency's
   * identity churns.
   *
   * `applyChartConfig` is not idempotent against live state: even in 'merge'
   * mode it calls `setSelectedSensors(chart's pens)` and
   * `setSingleScaleMode(chart.yAxisMode === 'single')` unconditionally
   * (useApplyChartConfig.ts:68-71, 99-101). So a second run silently reverts
   * pens the user added and the scale mode they chose — which reads as the
   * per-tag Ymin/Ymax labels vanishing, since single-scale mode renders one
   * shared label pair instead of one per tag.
   *
   * Guarding on identity alone was too fragile: any hook above returning a new
   * array or callback re-fired it.
   */
  const appliedChartIdRef = useRef<string | null>(null)

  /**
   * The config this mount hydrates from. Local state (a browser-refresh
   * survivor of the user's last state) takes precedence over the saved chart;
   * the saved config is the fallback/baseline. Note that once local state is
   * applied, the ref guard below keeps the saved chart from ever re-applying
   * on top of it.
   */
  const configChart = hydratedLocalState?.chart ?? chart

  const chartSensorSourceCount =
    configChart?.sourceConfigs.filter(
      (sourceConfig) => sourceConfig.type === 'sensor'
    ).length ?? 0
  const chartDerivedSourceCount =
    configChart?.sourceConfigs.filter(
      (sourceConfig) => sourceConfig.type === 'derivedValue'
    ).length ?? 0

  // Wait until the chart's sources can actually be resolved, otherwise the one
  // apply we allow would resolve to nothing.
  const canApplyChartConfig =
    !!configChart &&
    (chartSensorSourceCount === 0 || resolutionSensorEntries.length > 0) &&
    (chartDerivedSourceCount === 0 || derivedValueEntriesLoaded)

  useEffect(() => {
    if (!configChart || !canApplyChartConfig) return
    // Keyed with showAllPens so lifting the cap re-applies and picks up the
    // pens the first pass deliberately skipped.
    const applyKey = `${chartID}:${showAllPens}`
    if (appliedChartIdRef.current === applyKey) return
    appliedChartIdRef.current = applyKey

    applyChartConfig({
      chart: configChart,
      // Resolves the chart's own source ids, so the targeted pool is enough.
      sensorEntries: resolutionSensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode: 'merge',
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
      setThresholdFilters,
      setSmoothingConfigs,
      setResampleConfigs,
      setRegressionConfigs,
      setRulers,
      setPointAnnotations,
      setInteractionMode,
      setActiveSentinels,
      setSentinelVisibilityConfig,
    })

    // A chart that saved sentinels reopens with the Sentinels layer on —
    // mirroring `addSentinel`, since the layer toggle itself is not persisted
    // (dataLayers reset to DEFAULT_DATA_LAYERS every mount) and a restored
    // overlay behind a default-off layer would silently never render. For
    // users without the gecko-internal Sentinels UI this is harmless: scoring
    // is fetched per-sentinel and failures render nothing.
    if (
      configChart.sourceConfigs.some(
        (sourceConfig) => sourceConfig.type === 'sentinel'
      )
    ) {
      setDataLayers((prev) =>
        prev.map((layer) =>
          layer.id === 'sentinels' ? { ...layer, enabled: true } : layer
        )
      )
    }

    // View-only extras the saved-chart format cannot express. `applyChartConfig`
    // infers auto-scale from the presence of custom ranges; the explicit flag
    // (and the exact, un-rounded ranges) stored alongside the local state win.
    if (hydratedLocalState) {
      setChartName(configChart.name ?? '')
      const view = hydratedLocalState.view
      if (view?.autoScaleMode !== undefined) {
        setAutoScaleMode(view.autoScaleMode)
      }
      if (view?.customAxisRanges) {
        const ranges = view.customAxisRanges
        setCustomAxisRanges((prev) => ({ ...prev, ...ranges }))
      }
    }
    // Intentionally keyed off `chartID` via the ref guard above rather than
    // re-running on every identity change of these callbacks/arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configChart, chartID, canApplyChartConfig, showAllPens])

  // Sync the chart's persisted name into local state once the chart resolves.
  // Empty string means "not named yet" — the header turns that into a
  // placeholder. Skipped when local state hydrated: its name (set above) takes
  // precedence over the saved one.
  useEffect(() => {
    if (hydratedLocalState) return
    if (chart && typeof chart.name === 'string') {
      setChartName(chart.name)
    }
  }, [chart, hydratedLocalState])

  /** Tracks which sensor IDs from initialSensorIds have already been applied to selectedSensors */
  const appliedSensorIdsRef = useRef<Set<string>>(new Set())

  const [appliedOverrideSensorIdsKey, setAppliedOverrideSensorIdsKey] =
    useState<string | null>(null)
  // The ids the override last controlled — used to tell the user's manually
  // added sensors (to preserve) apart from override-removed ones (to drop).
  const appliedOverrideIdSetRef = useRef<Set<string>>(new Set())
  const [appliedOverrideTimeRangeKey, setAppliedOverrideTimeRangeKey] =
    useState<string | null>(null)

  useEffect(() => {
    if (
      !seedInitialSensorIds ||
      seedInitialSensorIds.length === 0 ||
      resolutionSensorEntries.length === 0 ||
      chart
    ) {
      return
    }

    const newIds = seedInitialSensorIds.filter(
      (id) => !appliedSensorIdsRef.current.has(id)
    )
    if (newIds.length === 0) return

    const newSensors = resolutionSensorEntries.filter((se) =>
      newIds.includes(se.id)
    )
    if (newSensors.length === 0) return

    for (const id of seedInitialSensorIds) {
      appliedSensorIdsRef.current.add(id)
    }

    setSelectedSensors((prev) => {
      const existingIds = new Set(prev.map((s) => s.id))
      const toAdd = newSensors.filter((s) => !existingIds.has(s.id))
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev
    })
  }, [seedInitialSensorIds, resolutionSensorEntries, chart, setSelectedSensors])

  /**
   * Apply caller-provided line colors, re-applying when the caller *changes* a
   * color for a sensor (e.g. claims reassigns the related-pen palette when a pen
   * is removed). We track the last color we applied per id, so a color the user
   * manually changed (caller value unchanged) is left alone, but a new caller
   * value does take effect.
   */
  const appliedSensorColorsRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    if (!initialSensorColors) return
    const pending: Record<string, string> = {}
    for (const [id, color] of Object.entries(initialSensorColors)) {
      if (appliedSensorColorsRef.current.get(id) !== color) {
        pending[id] = color
        appliedSensorColorsRef.current.set(id, color)
      }
    }
    if (Object.keys(pending).length === 0) return
    setCustomSensorColors((prev) => ({ ...prev, ...pending }))
  }, [initialSensorColors, setCustomSensorColors])

  const clearAppliedOverridePens = useCallback(() => {
    const prevOverride = appliedOverrideIdSetRef.current
    if (prevOverride.size === 0) return
    appliedOverrideIdSetRef.current = new Set()
    setAppliedOverrideSensorIdsKey(JSON.stringify([]))
    setSelectedSensors((prev) => prev.filter((s) => !prevOverride.has(s.id)))
  }, [setSelectedSensors])

  useEffect(() => {
    if (!overrideSensorIds) {
      return
    }
    if (overrideSensorIds.length === 0) {
      if (preserveManualSensors) clearAppliedOverridePens()
      return
    }

    if (
      resolutionSensorEntries.length === 0 ||
      (!disableSaving && !transient && chart === undefined)
    ) {
      return
    }

    const overrideKey = JSON.stringify([...overrideSensorIds].sort())
    if (appliedOverrideSensorIdsKey === overrideKey) {
      return
    }

    // Preserve the caller's requested order (e.g. claims put the primary sensor
    // first so it stays at the top of the legend) rather than the order sensors
    // happen to arrive in from the entries fetch.
    const overrideOrder = new Map(overrideSensorIds.map((id, i) => [id, i]))
    const nextSensors = resolutionSensorEntries
      .filter((sensorEntry) => overrideOrder.has(sensorEntry.id))
      .sort(
        (a, b) =>
          (overrideOrder.get(a.id) ?? 0) - (overrideOrder.get(b.id) ?? 0)
      )

    if (nextSensors.length === 0) {
      return
    }

    const prevOverride = appliedOverrideIdSetRef.current
    setAppliedOverrideSensorIdsKey(overrideKey)
    appliedOverrideIdSetRef.current = new Set(overrideSensorIds)

    if (!preserveManualSensors) {
      setSelectedSensors(nextSensors)
      return
    }

    // Preserve sensors the user added manually (present in the selection but not
    // controlled by the previous override) across this override change, while
    // the override stays add/remove-authoritative for the sensors it controls.
    setSelectedSensors((prev) => {
      const overrideIds = new Set(nextSensors.map((s) => s.id))
      const manual = prev.filter(
        (s) => !prevOverride.has(s.id) && !overrideIds.has(s.id)
      )
      return [...nextSensors, ...manual]
    })
  }, [
    overrideSensorIds,
    appliedOverrideSensorIdsKey,
    resolutionSensorEntries,
    disableSaving,
    transient,
    chart,
    preserveManualSensors,
    clearAppliedOverridePens,
    setSelectedSensors,
  ])

  useEffect(() => {
    if (!overrideTimeRange) {
      return
    }

    if (!disableSaving && !transient && chart === undefined) {
      return
    }

    const overrideKey = buildTimeRangeKey(overrideTimeRange)
    if (appliedOverrideTimeRangeKey === overrideKey) {
      return
    }

    const from = dayjs(overrideTimeRange.from)
    const to = dayjs(overrideTimeRange.to)
    if (!from.isValid() || !to.isValid()) {
      return
    }

    const timeRangeSeconds = Math.max(1, to.diff(from, 'seconds'))
    const { interval, interval_unit } =
      overrideTimeRange.interval && overrideTimeRange.intervalUnit
        ? {
            interval: overrideTimeRange.interval,
            interval_unit: overrideTimeRange.intervalUnit,
          }
        : TimeSeriesService.getIntervalForPenCount(
            timeRangeSeconds,
            selectedSensors.length
          )
    const nextTimeRange: TimeRange = {
      from: from.toISOString(),
      to: to.toISOString(),
      preset: 'custom',
      interval,
      intervalUnit: interval_unit,
    }

    setAppliedOverrideTimeRangeKey(overrideKey)
    setDateMode('absolute')
    setLiveTail(false)
    setTimeRange(nextTimeRange)
  }, [
    overrideTimeRange,
    appliedOverrideTimeRangeKey,
    disableSaving,
    transient,
    chart,
    selectedSensors.length,
    setDateMode,
    setLiveTail,
    setTimeRange,
  ])

  const appliedOverrideAxisRangesKey = useRef<string | null>(null)
  useEffect(() => {
    if (!overrideCustomAxisRanges) return
    const key = JSON.stringify(overrideCustomAxisRanges)
    if (appliedOverrideAxisRangesKey.current === key) return
    appliedOverrideAxisRangesKey.current = key
    if (!hasAnyCustomBound(overrideCustomAxisRanges)) return
    setCustomAxisRanges((prev) => ({ ...prev, ...overrideCustomAxisRanges }))
    setAutoScaleMode(false)
  }, [overrideCustomAxisRanges, setAutoScaleMode, setCustomAxisRanges])

  const appliedOverrideSharedAxisKey = useRef<string | null>(null)
  useEffect(() => {
    if (!overrideSharedAxisRange) return
    const key = JSON.stringify(overrideSharedAxisRange)
    if (appliedOverrideSharedAxisKey.current === key) return
    appliedOverrideSharedAxisKey.current = key
    if (
      !hasAnyCustomBound({ [SINGLE_SCALE_AXIS_ID]: overrideSharedAxisRange })
    ) {
      return
    }
    setSingleScaleMode(true)
    setCustomAxisRanges((prev) => ({
      ...prev,
      [SINGLE_SCALE_AXIS_ID]: overrideSharedAxisRange,
    }))
    setAutoScaleMode(false)
  }, [
    overrideSharedAxisRange,
    setAutoScaleMode,
    setCustomAxisRanges,
    setSingleScaleMode,
  ])

  const appliedOverrideHiddenIdsRef = useRef<string[] | null>(null)
  useEffect(() => {
    if (!overrideHiddenSensorIds) return
    const previous = appliedOverrideHiddenIdsRef.current
    if (
      previous &&
      JSON.stringify([...previous].sort()) ===
        JSON.stringify([...overrideHiddenSensorIds].sort())
    ) {
      return
    }
    appliedOverrideHiddenIdsRef.current = overrideHiddenSensorIds
    const hidden = new Set(overrideHiddenSensorIds)
    const shownAgain = (previous ?? []).filter((id) => !hidden.has(id))
    if (!hidden.size && !shownAgain.length) return
    setSensorVisibilityConfig((prev) => ({
      ...prev,
      ...Object.fromEntries(shownAgain.map((id) => [id, true])),
      ...Object.fromEntries([...hidden].map((id) => [id, false])),
    }))
  }, [overrideHiddenSensorIds, setSensorVisibilityConfig])

  /**
   * Latch: once initialization has settled for this chart id, a config-named
   * pen disappearing from the selection means the user REMOVED it — it must
   * not flip the provider back to "initializing", which would both re-show
   * the chart spinner and permanently gate the local-state writer off (the
   * removal would never persist and a refresh would resurrect the pen).
   */
  const settledChartIdRef = useRef<string | null>(null)

  const isInitializing = useMemo(() => {
    const dvLoaded =
      derivedValueEntriesLoaded || derivedValueEntriesError !== undefined
    // Gate on the targeted pen fetch, not the site-wide sensor list.
    // `configChart`, not `chart`: initialization must settle against the config
    // this mount actually hydrates from. With local state hydrated, the saved
    // chart may name pens the local state has since removed — matching against
    // the saved chart would then wait forever for a selection that never comes.
    const result = getScatterplotIsInitializing({
      chart: configChart,
      chartError,
      isLoadingChart,
      sensorEntries: resolutionSensorEntries,
      sensorEntriesError: resolutionSensorEntriesError ?? null,
      sensorEntriesLoaded: resolutionSensorEntriesLoaded,
      isLoadingSensorEntries: isLoadingResolutionSensorEntries,
      derivedValueEntries,
      derivedValueEntriesLoaded: dvLoaded,
      isLoadingDerivedValueEntries,
      // The seed-derived value, not the raw prop: when local state hydrated we
      // never apply the seed, so gating on it would hold the chart initializing
      // forever.
      initialSensorIds: seedInitialSensorIds,
      overrideSensorIds,
      overrideTimeRange,
      selectedSensors,
      derivedValues,
      appliedInitialSensorIds: appliedSensorIdsRef.current,
      appliedOverrideSensorIdsKey,
      appliedOverrideTimeRangeKey,
      disableSaving,
      transient,
      hasSettledForChart: settledChartIdRef.current === chartID,
    })
    if (!result) {
      settledChartIdRef.current = chartID
    }
    return result
  }, [
    chartID,
    configChart,
    chartError,
    isLoadingChart,
    resolutionSensorEntries,
    resolutionSensorEntriesError,
    resolutionSensorEntriesLoaded,
    isLoadingResolutionSensorEntries,
    derivedValueEntries,
    derivedValueEntriesLoaded,
    derivedValueEntriesError,
    isLoadingDerivedValueEntries,
    seedInitialSensorIds,
    overrideSensorIds,
    overrideTimeRange,
    appliedOverrideSensorIdsKey,
    appliedOverrideTimeRangeKey,
    selectedSensors,
    derivedValues,
    disableSaving,
    transient,
  ])

  // Perf marks for the cold-load critical path. `pensResolved` is the moment the
  // chart config, the site-wide sensor list and derived values have all landed —
  // i.e. the earliest a time-series request can be issued.
  useEffect(() => {
    perfMark('providerMounted')
  }, [])

  useEffect(() => {
    if (!isInitializing) {
      perfMark('pensResolved')
    }
  }, [isInitializing])

  useEffect(() => {
    if (allSensorEntryWrappers !== undefined) {
      perfMark('siteSensorListLoaded')
    }
  }, [allSensorEntryWrappers])

  useEffect(() => {
    if (chart !== undefined) {
      perfMark('chartConfigLoaded')
    }
  }, [chart])

  useEffect(() => {
    if (derivedValueEntriesLoaded) {
      perfMark('derivedValuesLoaded')
    }
  }, [derivedValueEntriesLoaded])

  /**
   * Reset the DATE ONLY: back to the saved chart's date config (resolved to a
   * concrete range), or to the default latest-1-week view when the chart has
   * no saved date config / is unsaved. Everything else — pens, colors, scales,
   * visibility — is deliberately preserved; full-config revert lives in
   * `revertChanges`, not here.
   */
  const resetViewport = useCallback(() => {
    loadTriggerRef.current = 'other'

    const savedDateState = chart
      ? loadDateConfigFromChart(chart, cadenceSeconds)
      : null

    const nextDateState = savedDateState ?? {
      ...buildDefaultRelativeDateState(selectedSensors.length),
      cadenceSeconds,
    }

    // Only preview-pan the x-axis when time is on it.
    if (xAxisSensorId === null) {
      const previewMin = Date.parse(nextDateState.timeRange.from)
      const previewMax = Date.parse(nextDateState.timeRange.to)
      if (Number.isFinite(previewMin) && Number.isFinite(previewMax)) {
        setTimeXAxisPreviewRange({ min: previewMin, max: previewMax })
      } else {
        setTimeXAxisPreviewRange(null)
      }
    } else {
      setTimeXAxisPreviewRange(null)
    }

    setDateMode(nextDateState.dateMode)
    setLiveTail(nextDateState.liveTail)
    setTimeRange({ ...nextDateState.timeRange })
    setRelativeStart({ ...nextDateState.relativeStart })
    setRelativeEnd({ ...nextDateState.relativeEnd })
    setCadenceSeconds(nextDateState.cadenceSeconds)
  }, [
    chart,
    cadenceSeconds,
    loadTriggerRef,
    selectedSensors.length,
    setCadenceSeconds,
    setDateMode,
    setLiveTail,
    setRelativeEnd,
    setRelativeStart,
    setTimeRange,
    setTimeXAxisPreviewRange,
    xAxisSensorId,
  ])

  // Build current chart config from live state
  const computedSourceConfigs = useMemo(
    () =>
      buildSourceConfigsFromState(
        selectedSensors,
        customSensorColors,
        derivedValues.filter((dv) => !dv.id.startsWith(COMPUTED_PEN_PREFIX)),
        customAxisRanges,
        sensorVisibilityConfig,
        autoScaleMode,
        activeSentinels,
        sentinelVisibilityConfig
      ),
    [
      selectedSensors,
      customSensorColors,
      derivedValues,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode,
      activeSentinels,
      sentinelVisibilityConfig,
    ]
  )

  const computedYAxisMode = useMemo(
    () => buildYAxisModeFromState(singleScaleMode),
    [singleScaleMode]
  )

  const computedDateConfig = useMemo(
    () =>
      buildDateConfigFromState(
        dateMode,
        timeRange,
        relativeStart,
        relativeEnd,
        cadenceSeconds
      ),
    [dateMode, timeRange, relativeStart, relativeEnd, cadenceSeconds]
  )

  const computedDataProcessingConfigs = useMemo(
    () =>
      buildDataProcessingConfigsFromState(
        thresholdFilters,
        smoothingConfigs,
        regressionConfigs,
        resampleConfigs
      ),
    [thresholdFilters, smoothingConfigs, regressionConfigs, resampleConfigs]
  )

  /**
   * Local State writer: continuously mirror the live chart state into
   * localStorage (debounced) so a browser refresh restores it. Gated on
   * initialization so a half-hydrated mount can never clobber good state.
   */
  useEffect(() => {
    if (!localStateEnabled || isInitializing) return
    // A chart id whose resolution FAILED leaves nothing trustworthy to mirror:
    // writing would persist this mount's empty state under the chart's key and
    // mask the healthy saved chart on the next load. Hydrated local state is
    // the exception — the mount then holds real state worth persisting. (`chart`
    // present means a revalidation error, not a failed resolution: keep writing.)
    if (chartError && !chart && !hydratedLocalState) return
    // When there is a config to hydrate from, never write before the one-shot
    // apply has run: a slow pen fetch must not let a half-initialized mount
    // overwrite good stored state. (Ref read is safe: the apply itself changes
    // state, which re-runs this effect.)
    if (configChart && appliedChartIdRef.current === null) return
    // While the pen cap is truncating the requested pens (a >MAX_PENS chart
    // the user has not expanded via "Show all pens"), the live selection is a
    // capped SUBSET of the saved chart. Persisting it would make the
    // truncation sticky: hydration prefers local state over the saved chart,
    // so the capped subset would silently win over the full saved pen list on
    // every subsequent load — and "Show all pens" would then just re-apply
    // the truncated config. Skip writing until the cap is lifted; this is the
    // one guard, so hydration never needs to second-guess a stored subset.
    if (penCap.isCapped) return

    const timer = setTimeout(() => {
      writeChartLocalState(
        localStateKeyRef.current,
        buildChartPatchData({
          name: chartName,
          sourceConfigs: computedSourceConfigs,
          yAxisMode: computedYAxisMode,
          dateConfig: computedDateConfig,
          dataProcessingConfigs: computedDataProcessingConfigs,
          rulers,
          pointAnnotations,
          interactionMode,
        }),
        { autoScaleMode, customAxisRanges }
      )
    }, LOCAL_STATE_WRITE_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [
    localStateEnabled,
    isInitializing,
    chart,
    chartError,
    hydratedLocalState,
    configChart,
    penCap.isCapped,
    chartName,
    computedSourceConfigs,
    computedYAxisMode,
    computedDateConfig,
    computedDataProcessingConfigs,
    rulers,
    pointAnnotations,
    interactionMode,
    autoScaleMode,
    customAxisRanges,
  ])

  /**
   * On a successful save, local and saved state converge: rewrite local state
   * to exactly what was saved. A transient/ad-hoc chart promoted to a real id
   * migrates off the `adhoc` key so the key follows the chart from here on.
   *
   * A scoped surface (doc-viewer tab) is the exception: the tab stays open
   * under its tab id, so its state stays keyed by the tab id — we rewrite the
   * SAME key rather than migrate to the chart id. (A migration would strand the
   * state under a chart-id key the still-tab-scoped writer never reads again.)
   */
  const handleChartSavedInternal = useCallback(
    async (savedChartID: string, isNewChart: boolean) => {
      if (localStateEnabled) {
        const nextKey = localStateScope
          ? localStateKeyRef.current
          : chartLocalStateKey(siteID, savedChartID)
        writeChartLocalState(
          nextKey,
          buildChartPatchData({
            name: chartName,
            sourceConfigs: computedSourceConfigs,
            yAxisMode: computedYAxisMode,
            dateConfig: computedDateConfig,
            dataProcessingConfigs: computedDataProcessingConfigs,
            rulers,
            pointAnnotations,
            interactionMode,
          }),
          { autoScaleMode, customAxisRanges }
        )
        if (localStateKeyRef.current !== nextKey) {
          clearChartLocalState(localStateKeyRef.current)
          localStateKeyRef.current = nextKey
        }
      }
      await onChartSaved?.(savedChartID, isNewChart)
    },
    [
      localStateEnabled,
      localStateScope,
      siteID,
      chartName,
      computedSourceConfigs,
      computedYAxisMode,
      computedDateConfig,
      computedDataProcessingConfigs,
      rulers,
      pointAnnotations,
      interactionMode,
      autoScaleMode,
      customAxisRanges,
      onChartSaved,
    ]
  )

  const {
    saveChart,
    hasUnsavedChanges,
    hasUnsavedAnnotationChanges,
    lastSavedAt,
    isNewChart,
  } = useChartSave({
    chartID: disableSaving ? '' : chartID,
    name: chartName,
    selectedSourceConfigs: computedSourceConfigs,
    yAxisMode: computedYAxisMode,
    dateConfig: computedDateConfig,
    dataProcessingConfigs: computedDataProcessingConfigs,
    rulers,
    pointAnnotations,
    interactionMode,
    tags: {
      site: siteID,
      organization: orgID,
    },
    derivedValueIdsToDeleteRef: derivedValueIdsToDeleteRef,
    onChartSaved: handleChartSavedInternal,
    transient,
  })

  const revertChanges = useCallback(() => {
    derivedValueIdsToDeleteRef.current.clear()

    // A never-saved view reverts to a blank new view; applyChartConfig is a
    // no-op without a chart, so the reset is explicit.
    if (!chart) {
      setSelectedSensors([])
      setDerivedValues([])
      setCustomSensorColors({})
      setSensorVisibilityConfig({})
      setCustomAxisRanges({})
      setAutoScaleMode(true)
      setSingleScaleMode(false)
      setThresholdFilters({})
      setSmoothingConfigs({})
      setResampleConfigs({})
      setRegressionConfigs([])
      setRulers([])
      setPointAnnotations([])
      setInteractionMode('none')
      setActiveSentinels([])
      setSentinelVisibilityConfig({})
      setChartName('')
      const blankDateState = buildDefaultRelativeDateState(0)
      setDateMode(blankDateState.dateMode)
      setLiveTail(blankDateState.liveTail)
      setTimeRange({ ...blankDateState.timeRange })
      setRelativeStart({ ...blankDateState.relativeStart })
      setRelativeEnd({ ...blankDateState.relativeEnd })
      return
    }

    setChartName(chart.name ?? '')
    applyChartConfig({
      chart,
      sensorEntries: resolutionSensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode: 'replace',
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
      setThresholdFilters,
      setSmoothingConfigs,
      setResampleConfigs,
      setRegressionConfigs,
      setRulers,
      setPointAnnotations,
      setInteractionMode,
      setActiveSentinels,
      setSentinelVisibilityConfig,
    })

    // Match the hydration path: reverting to a chart that saved sentinels
    // re-lights the Sentinels layer so the restored bands actually show.
    if (
      chart?.sourceConfigs.some(
        (sourceConfig) => sourceConfig.type === 'sentinel'
      )
    ) {
      setDataLayers((prev) =>
        prev.map((layer) =>
          layer.id === 'sentinels' ? { ...layer, enabled: true } : layer
        )
      )
    }
  }, [
    chart,
    resolutionSensorEntries,
    derivedValueEntries,
    cadenceSeconds,
    applyChartConfig,
    setSelectedSensors,
    setDerivedValues,
    setCustomSensorColors,
    setSensorVisibilityConfig,
    setCustomAxisRanges,
    setAutoScaleMode,
    setSingleScaleMode,
    setDateMode,
    setTimeRange,
    setLiveTail,
    setRelativeStart,
    setRelativeEnd,
    setCadenceSeconds,
    setThresholdFilters,
    setSmoothingConfigs,
    setResampleConfigs,
    setRegressionConfigs,
    setRulers,
    setPointAnnotations,
    setInteractionMode,
  ])

  const reportedViewRef = useRef<string | null>(null)
  useEffect(() => {
    if (!onViewChange || isInitializing) return
    const sensorIds = selectedSensors.map((sensor) => sensor.id)
    const view: EmbeddedTrendView = {
      start: timeRange.from,
      end: timeRange.to,
      interval: timeRange.interval,
      intervalUnit: timeRange.intervalUnit,
      sensorIds,
      hiddenSensorIds: sensorIds.filter(
        (id) => sensorVisibilityConfig[id] === false
      ),
      customAxisRanges,
      singleScale: singleScaleMode,
    }
    const key = JSON.stringify(view)
    if (reportedViewRef.current === key) return
    reportedViewRef.current = key
    onViewChange(view)
  }, [
    onViewChange,
    isInitializing,
    timeRange,
    selectedSensors,
    sensorVisibilityConfig,
    customAxisRanges,
    singleScaleMode,
  ])

  return (
    <CursorDataProvider>
      <ScatterplotContext.Provider
        value={{
          siteID,
          organizationID,
          chartID,
          transient,
          chartData,
          interpolate,
          setInterpolate,
          isLoading,
          isInitializing,
          liveTail,
          setLiveTail,
          derivedValues,
          setDerivedValues,
          regressionConfigs,
          setRegressionConfigs,
          regressionResults,
          setRegressionResults,
          unitMapping,
          computedScalePairs,
          sensorRanges: sensorRangesWithExperiment,
          localThresholdOverrides,
          setLocalThresholdOverrides,
          thresholdFilters,
          setThresholdFilters,
          filteredPoints,
          smoothingConfigs,
          setSmoothingConfigs,
          resampleConfigs,
          setResampleConfigs,
          singleScaleMode,
          autoScaleMode,
          setAutoScaleMode,
          handleScaleModeToggle,
          handleClearAxisRange,
          handleAutoScaleAll,
          connectPoints,
          setConnectPoints,
          xAxisSensorId,
          setXAxisSensorId,
          customAxisRanges,
          customXAxisRange,
          setCustomXAxisRange,
          sensorAxisMapping,
          fetchRawData,
          prefetchTimeRange,
          handleRemoveDerivedValue,
          createDerivedValues,
          dataLayers,
          setDataLayers,
          setCustomAxisRanges,
          expandedAxes,
          setExpandedAxes,
          setSensorAxisAssignment,
          handleYMinChange,
          handleYMaxChange,
          timeRange,
          setTimeRange,
          selectedSensors,
          setSelectedSensors,
          sensorEntries,
          penCap,
          sensorVisibilityConfig,
          setSensorVisibilityConfig,
          dateMode,
          setDateMode,
          exitLiveTail,
          relativeStart,
          setRelativeStart,
          relativeEnd,
          setRelativeEnd,
          customSensorColors,
          handleChangeColor,
          cadenceSeconds,
          setCadenceSeconds,
          chartName,
          setChartName,
          saveChart,
          revertChanges,
          hasUnsavedChanges,
          hasUnsavedAnnotationChanges,
          isNewChart,
          lastSavedAt,
          isResizing,
          setIsResizing,
          highlightedSensorId,
          setHighlightedSensorId,
          secondaryHighlightedSensorIds,
          setSecondaryHighlightedSensorIds,
          hideAllAlerts,
          setHideAllAlerts,
          visibleLabelIds,
          setVisibleLabelIds,
          deemphasizedSensorIds: deemphasizedSensorIds ?? EMPTY_DEEMPHASIZED,
          timeXAxisPreviewRange,
          setTimeXAxisPreviewRange,
          resetViewport,
          pendingSensorIds: initialSensorIds,
          loadTriggerRef,
          rulers,
          setRulers,
          pointAnnotations,
          setPointAnnotations,
          interactionMode,
          setInteractionMode,
          activeSentinels,
          addSentinel,
          removeSentinel,
          setSentinelMethod,
          sentinelVisibilityConfig,
          setSentinelVisibilityConfig,
          computedOverlay,
        }}>
        {/*
         * Experiment Lab session state lives at the provider level (not in
         * the sidebar tab) so switching tabs or closing the sidebar never
         * discards a configured experiment or a finished training run, and
         * so the chart can read the experiment overlays.
         */}
        <ExperimentLabProvider
          siteID={siteID}
          sensorEntryPool={sensorEntryPool}
          selectedSensors={selectedSensors}
          setSelectedSensors={setSelectedSensors}
          setSensorVisibilityConfig={setSensorVisibilityConfig}
          setCustomAxisRanges={setCustomAxisRanges}
          reportScaleExtents={reportExperimentYExtents}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          exitLiveTail={exitLiveTail}>
          {/*
           * The customer-facing Sentinels tab keeps its build/plan/monitor
           * session state here too (same reason: survive tab switches), nested
           * so it can read the same pen pool. Its chart side effects are wired
           * in the milestones step.
           */}
          <SentinelStudioProvider
            siteID={siteID}
            sensorEntryPool={sensorEntryPool}>
            {children}
          </SentinelStudioProvider>
        </ExperimentLabProvider>
      </ScatterplotContext.Provider>
    </CursorDataProvider>
  )
}

export const ScatterplotContext = createContext<ScatterplotProviderReturnType>(
  null!
)

export const useScatterplotContext = (): ScatterplotProviderReturnType => {
  const context = useContext(ScatterplotContext)
  if (!context) {
    throw new Error('useScatterplot must be used within a ScatterplotProvider')
  }
  return context
}
